/**
 * 레이아웃 스타일 정의 유틸리티
 * Single Responsibility: 스타일 객체 생성만 담당
 */ import { SIDEBAR_WIDTHS } from '@edusense/shared-constants';
export const createLayoutStyles = (options)=>{
    const { hasRightSidebar, rightSidebarWidth, enableResize = false, isMobileView = false, isTabletView = false, viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1920 } = options;
    // 🎯 2024 최신 방식: CSS Grid fr 단위와 CSS 변수 활용
    const getSidebarWidth = ()=>{
        if (isMobileView) return '0fr'; // 모바일: fr 단위로 자연스럽게 숨김
        if (isTabletView) return `minmax(${SIDEBAR_WIDTHS.ICON_ONLY}px, ${SIDEBAR_WIDTHS.ICON_ONLY}px)`; // 태블릿: 고정 아이콘 너비
        // 🔥 데스크톱에서는 표준 너비 유지 (275px)
        // EXPANDED(360px)는 너무 넓어서 사용하지 않음
        return `${SIDEBAR_WIDTHS.STANDARD}px`;
    };
    // 🔧 2024 최신 Grid 템플릿 - 자동 반응형
    const getGridTemplate = ()=>{
        if (isMobileView) {
            // 모바일: 단순 1열 레이아웃 (오른쪽 패널은 오버레이에서 처리하므로 기본 1열)
            return '1fr';
        }
        // 🔥 최신 Grid 기법: fr 단위 기반 유연한 레이아웃
        // - 사이드바: 0.2fr (전체의 20%, 최소 200px 보장) 또는 태블릿 68px 고정
        // - 메인: 1fr (남은 공간), 최소폭을 0으로 낮춰 총합이 뷰포트를 넘지 않도록 함
        // - 오른쪽: clamp로 최소/이상적/최대를 보장하여 잘림 방지
        if (hasRightSidebar) {
            // 태블릿 구간: 좌 68px + 메인 최소 300px 보장 + 우측 패널은 40vw 이하로 축소 허용
            if (isTabletView) {
                return `${SIDEBAR_WIDTHS.ICON_ONLY}px minmax(300px, 1fr) clamp(250px, ${rightSidebarWidth}px, 40vw)`;
            }
            // 데스크톱: 좌측은 표준 너비(275px), 메인은 최소 400px 보장, 우측은 33vw 이하로 제한
            return `${SIDEBAR_WIDTHS.STANDARD}px minmax(400px, 1fr) clamp(280px, ${rightSidebarWidth}px, 33vw)`;
        }
        // 오른쪽 패널이 없는 경우
        if (isTabletView) {
            return `${SIDEBAR_WIDTHS.ICON_ONLY}px minmax(0, 1fr)`;
        }
        // 데스크톱: 표준 너비 유지
        return `${SIDEBAR_WIDTHS.STANDARD}px minmax(0, 1fr)`;
    };
    // 🎯 sidebarStyle의 width를 위한 별도 계산
    const sidebarWidthForStyle = isMobileView ? '0px' : isTabletView ? `${SIDEBAR_WIDTHS.ICON_ONLY}px` : '100%';
    const containerStyle = {
        // 🔧 항상 그리드 레이아웃 사용하여 사이드바와 메인 콘텐츠 겹침 방지
        display: 'grid',
        // 🔧 반응형 그리드 템플릿 적용
        gridTemplateColumns: getGridTemplate(),
        height: '100vh',
        fontFamily: "'Pretendard', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        backgroundColor: 'var(--theme-paper, #ffffff)',
        color: 'var(--theme-text, #1f2937)',
        // 🔧 트랜지션 추가로 부드러운 리사이즈
        transition: 'grid-template-columns 0.3s ease-out',
        overflow: 'hidden',
        // 🔥 Container Query 지원 (2024 최신 기술)
        containerType: 'inline-size'
    };
    const sidebarStyle = {
        // 🔧 그리드 첫 번째 컬럼에 배치
        gridColumn: '1 / 2',
        width: sidebarWidthForStyle,
        minHeight: '100vh',
        display: isMobileView ? 'none' : 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        padding: 0,
        borderRight: isMobileView ? 'none' : '1px solid var(--theme-border, #e5e7eb)',
        overflowY: 'auto',
        overflowX: 'hidden',
        backgroundColor: 'var(--theme-paper, #ffffff)',
        position: 'relative',
        zIndex: 10,
        // 🔧 트랜지션 추가로 부드러운 너비 변경
        transition: 'width 0.3s ease-out, padding 0.3s ease-out'
    };
    const mainStyle = {
        // 🔧 그리드 아이템으로서 자동으로 메인 콘텐츠 영역 차지
        gridColumn: isMobileView ? hasRightSidebar ? '1 / 2' : '1 / -1' // 모바일: 첫 번째 컬럼부터
         : hasRightSidebar ? '2 / 3' : '2 / -1',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        minWidth: 0,
        position: 'relative',
        // 🔧 메인 콘텐츠도 부드럽게 전환
        transition: 'all 0.3s ease-out'
    };
    const headerStyle = {
        height: '64px',
        backgroundColor: 'var(--theme-paper, #ffffff)',
        borderBottom: '1px solid var(--theme-border, #e5e7eb)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 1.5rem',
        flexShrink: 0
    };
    const contentStyle = {
        flex: 1,
        backgroundColor: 'var(--theme-background, #f9fafb)',
        overflow: 'hidden',
        paddingBottom: isMobileView ? '52px' : '0'
    };
    const rightSidebarStyle = {
        // 🔧 반응형 그리드 컬럼 배치
        gridColumn: isMobileView ? '2 / 3' : '3 / 4',
        position: 'relative',
        backgroundColor: 'var(--theme-paper, #ffffff)',
        borderLeft: '1px solid var(--theme-border, #e5e7eb)',
        overflow: 'auto',
        cursor: 'default',
        // 🔧 오른쪽 사이드바도 부드럽게 전환
        transition: 'all 0.3s ease-out'
    };
    const mobileNavStyle = {
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        height: '52px',
        backgroundColor: 'var(--theme-paper, #ffffff)',
        borderTop: '1px solid var(--theme-border, #e5e7eb)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-around',
        zIndex: 50
    };
    return {
        containerStyle,
        sidebarStyle,
        mainStyle,
        headerStyle,
        contentStyle,
        rightSidebarStyle,
        mobileNavStyle
    };
};
